import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../lib/translations';
import { LanguageSelector } from '../components/LanguageSelector';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { language } = useLanguage();

  const token = searchParams.get('token');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (password !== confirmPassword) {
      setStatus('error');
      setMessage(t('passwordsDoNotMatch', language));
      return;
    }

    if (password.length < 6) {
      setStatus('error');
      setMessage(t('passwordTooShort', language));
      return;
    }

    if (!token) {
      setStatus('error');
      setMessage(t('invalidResetLink', language));
      return;
    }

    setStatus('loading');

    try {
      await api.resetPassword(token, password);
      setStatus('success');
      setMessage(t('passwordResetSuccess', language));
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setStatus('error');
      setMessage(err.message || t('passwordResetFailed', language));
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card card-large w-full max-w-md fade-in text-center">
          <span className="text-5xl block mb-4">❌</span>
          <h2 className="text-xl font-semibold text-red-700 mb-2">{t('invalidResetLink', language)}</h2>
          <p className="text-gray-600 mb-6">{t('requestNewResetLink', language)}</p>
          <Link to="/forgot-password" className="btn btn-primary">
            {t('requestNewLink', language)}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div style={{ position: 'absolute', top: '1rem', left: '1rem' }}>
        <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
          ← {t('backToLogin', language)}
        </Link>
      </div>
      <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
        <LanguageSelector />
      </div>
      <div className="card card-large w-full max-w-md fade-in">
        <div className="text-center mb-8">
          <h1 className="text-3xl mb-2" style={{ color: 'var(--primary)' }}>
            MindsetFamily
          </h1>
          <p className="text-gray-600">{t('resetPasswordSubtitle', language)}</p>
        </div>

        {status === 'success' ? (
          <div className="text-center py-8">
            <span className="text-5xl block mb-4">✅</span>
            <h2 className="text-xl font-semibold text-green-700 mb-2">{t('passwordReset', language)}</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">{t('redirectingToLogin', language)}</p>
          </div>
        ) : (
          <>
            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="label">{t('newPassword', language)}</label>
                <input
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>

              <div className="mb-6">
                <label className="label">{t('confirmPassword', language)}</label>
                <input
                  type="password"
                  className="input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-large w-full"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? t('loading', language) : t('resetPassword', language)}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
